import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI, soCreateAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, ArrowsLeftRight, Eye, Truck, Lightning,
  CaretUp, CaretDown, UploadSimple, Broom, DownloadSimple,
} from '@phosphor-icons/react';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import ItemSearchPicker from '@/components/ItemSearchPicker';

function SortableTH({ label, field, sortBy, sortDir, onSort }) {
  const isActive = sortBy === field;
  return (
    <th className="text-left p-3 select-none cursor-pointer hover:text-[#0F4C81]" onClick={() => onSort(field)} data-testid={`to-sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />) : <CaretDown size={10} className="opacity-30" />}
      </span>
    </th>
  );
}

export default function TransferOrdersPage() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branches, setBranches] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [fromBranchFilter, setFromBranchFilter] = useState('all');
  const [toBranchFilter, setToBranchFilter] = useState('all');
  const [transferType, setTransferType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [fromBranches, setFromBranches] = useState([]);
  const [toBranches, setToBranches] = useState([]);
  const [availStatuses, setAvailStatuses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deduping, setDeduping] = useState(false);

  const handleDedupe = async () => {
    if (!window.confirm('Remove duplicate transfer orders? This keeps one entry per transfer-order number and cannot be undone.')) return;
    setDeduping(true);
    try {
      const { data } = await billingOpsAPI.dedupeTransferOrders();
      if (data.removed > 0) {
        toast.success(`Removed ${data.removed} duplicate transfer order(s): ${data.before} → ${data.after}`);
      } else {
        toast.success(`No duplicates found — ${data.after} transfer orders (all unique)`);
      }
      loadTransfers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dedupe failed');
    } finally {
      setDeduping(false);
    }
  };

  const [form, setForm] = useState({
    from_branch_id: '', to_branch_id: '', transfer_date: new Date().toISOString().split('T')[0], notes: '',
  });
  const [lineItems, setLineItems] = useState([
    { item_id: '', name: '', product_code: '', quantity: 1, rate: 0, tax_percent: 0, serial_numbers: [], _serialInput: '', _availableSerials: [], _showSerialDropdown: false }
  ]);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, search, sort_by: sortBy, sort_dir: sortDir };
      if (fromBranchFilter !== 'all') params.from_branch = fromBranchFilter;
      if (toBranchFilter !== 'all') params.to_branch = toBranchFilter;
      if (transferType !== 'all') params.transfer_type = transferType;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await billingOpsAPI.listTransferOrders(params);
      setTransfers(data.transfers || []);
      setTotal(data.total || 0);
      if (data.from_branches) setFromBranches(data.from_branches);
      if (data.to_branches) setToBranches(data.to_branches);
      if (data.statuses) setAvailStatuses(data.statuses);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, perPage, search, sortBy, sortDir, fromBranchFilter, toBranchFilter, transferType, statusFilter, startDate, endDate]);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  useEffect(() => {
    (async () => {
      const [brRes, itemsRes] = await Promise.allSettled([
        soCreateAPI.branches(),
        soCreateAPI.items({ per_page: 2000 }),
      ]);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
    })();
  }, []);

  const resetForm = () => {
    setForm({ from_branch_id: '', to_branch_id: '', transfer_date: new Date().toISOString().split('T')[0], notes: '' });
    setLineItems([{ item_id: '', name: '', product_code: '', quantity: 1, rate: 0, tax_percent: 0, serial_numbers: [], _serialInput: '', _availableSerials: [], _showSerialDropdown: false }]);
  };

  const fetchSerialsForLine = useCallback(async (idx, itemId, itemName) => {
    if (!form.from_branch_id || !itemId) {
      setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, _availableSerials: [] } : li));
      return;
    }
    try {
      const branch = branches.find(b => b.id === form.from_branch_id);
      const { data } = await soCreateAPI.availableSerials({
        item_id: itemId, item_name: itemName || '', location: branch?.name || '',
      });
      setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, _availableSerials: data.serial_numbers || [] } : li));
    } catch (e) {
      setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, _availableSerials: [] } : li));
    }
  }, [form.from_branch_id, branches]);

  const selectCatalogItem = async (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      rate: item.cost_price || item.purchase_price || item.purchase_rate || 0, tax_percent: gstRate,
      serial_numbers: [], _serialInput: '', _availableSerials: [], _showSerialDropdown: false,
    } : li));
    // Fetch available serials from the source branch
    await fetchSerialsForLine(idx, item.id, item.name);
  };

  // Re-fetch serials for every line whenever the source branch changes (so the
  // in-stock serial dropdown appears even when the branch is picked after the items).
  useEffect(() => {
    lineItems.forEach((li, idx) => {
      if (li.item_id) fetchSerialsForLine(idx, li.item_id, li.name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.from_branch_id]);

  const toggleSerial = (idx, sn) => {
    setLineItems(prev => prev.map((li, i) => {
      if (i !== idx) return li;
      const selected = li.serial_numbers || [];
      const isSelected = selected.includes(sn);
      const updated = isSelected ? selected.filter(s => s !== sn) : [...selected, sn];
      return { ...li, serial_numbers: updated, _serialInput: updated.join(', ') };
    }));
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { item_id: '', name: '', product_code: '', quantity: 1, rate: 0, tax_percent: 0, serial_numbers: [], _serialInput: '', _availableSerials: [], _showSerialDropdown: false }]);
  };

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const fromBranch = branches.find(b => b.id === form.from_branch_id);
  const toBranch = branches.find(b => b.id === form.to_branch_id);
  const isInterState = fromBranch && toBranch && fromBranch.state_code !== toBranch.state_code;

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleCreate = async () => {
    if (!form.from_branch_id) { toast.error('Source branch is required'); return; }
    if (!form.to_branch_id) { toast.error('Destination branch is required'); return; }
    if (form.from_branch_id === form.to_branch_id) { toast.error('Source and destination must be different'); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code,
          quantity: parseFloat(li.quantity) || 1, rate: parseFloat(li.rate) || 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
          serial_numbers: li._serialInput ? li._serialInput.split(',').map(s => s.trim()).filter(Boolean) : [],
        })),
      };
      const { data } = await billingOpsAPI.createTransferOrder(payload);
      toast.success(`Transfer Order ${data.to_number} created!${data.is_inter_state ? ' (Inter-state: Invoice & Bill auto-generated)' : ''}`);
      setShowCreate(false);
      resetForm();
      loadTransfers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create transfer order');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6" data-testid="transfer-orders-page">
      <PageHeader title="Transfer Orders" subtitle="Manage inter-branch inventory transfers" />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search transfers..." className="pl-9" data-testid="to-search" />
        </div>
        <Select value={transferType} onValueChange={v => { setTransferType(v); setPage(1); }}>
          <SelectTrigger className="w-32" data-testid="to-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="inter">Inter-State</SelectItem>
            <SelectItem value="intra">Intra-State</SelectItem>
          </SelectContent>
        </Select>
        <SearchableSelect value={fromBranchFilter} onChange={v => { setFromBranchFilter(v); setPage(1); }} placeholder="From" testid="to-from-filter" buttonClassName="w-44"
                  options={[{ value: 'all', label: 'All From' }, ...fromBranches.map(b => ({ value: b, label: b }))]} />
        <SearchableSelect value={toBranchFilter} onChange={v => { setToBranchFilter(v); setPage(1); }} placeholder="To" testid="to-to-filter" buttonClassName="w-44"
                  options={[{ value: 'all', label: 'All To' }, ...toBranches.map(b => ({ value: b, label: b }))]} />
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32" data-testid="to-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {availStatuses.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#64748B]">{total.toLocaleString('en-IN')} transfers</span>
        <label className="ml-auto cursor-pointer">
          <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="to-bulk-upload-input"
            onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (!f) return;
              setUploading(true);
              try {
                const { data } = await billingOpsAPI.bulkUploadTransferOrders(f);
                toast.success(`Imported ${data.inserted} new transfer order(s) — ${data.already_present} already present, ${data.serials_moved} serials moved, ${data.duplicates_removed || 0} duplicates removed`);
                loadTransfers();
              } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
              finally { setUploading(false); }
            }} />
          <Button size="sm" variant="outline" asChild disabled={uploading}>
            <span><UploadSimple size={14} className="mr-1" /> {uploading ? 'Importing...' : 'Import from Zoho (.xlsx)'}</span>
          </Button>
        </label>
        <Button size="sm" variant="outline" data-testid="to-sample-file-btn"
          onClick={async () => {
            try {
              const { data } = await billingOpsAPI.downloadTransferOrderTemplate();
              const url = window.URL.createObjectURL(new Blob([data]));
              const a = document.createElement('a'); a.href = url; a.download = 'transfer_orders_template.xlsx';
              document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
            } catch { toast.error('Failed to download template'); }
          }}>
          <DownloadSimple size={14} className="mr-1" /> Sample File
        </Button>
        <Button size="sm" variant="outline" onClick={handleDedupe} disabled={deduping} data-testid="to-dedupe-btn">
          <Broom size={14} className="mr-1" /> {deduping ? 'Deduping...' : 'Dedupe'}
        </Button>
        <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="to-create-btn">
          <Plus size={14} className="mr-1" /> New Transfer Order
        </Button>
      </div>

      {/* Date range row */}
      <div className="mt-3 flex flex-wrap items-center gap-3 pt-2 border-t border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-[#64748B]">From</label>
          <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="w-40 h-9" data-testid="to-from-date" />
          <span className="text-[#64748B]">—</span>
          <label className="text-[10px] uppercase tracking-wide text-[#64748B]">To</label>
          <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="w-40 h-9" data-testid="to-to-date" />
        </div>
        <div className="h-6 w-px bg-[#E2E8F0]" />
        <div className="flex bg-[#F8FAFC] rounded-lg p-0.5">
          <button onClick={() => { const t = new Date().toISOString().split('T')[0]; setStartDate(t); setEndDate(t); setPage(1); }} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="to-quick-today">Today</button>
          <button onClick={() => { const d = new Date(); const first = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; const today = d.toISOString().split('T')[0]; setStartDate(first); setEndDate(today); setPage(1); }} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="to-quick-month">This Month</button>
          <button onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="to-quick-all">All Time</button>
        </div>
        {(search || fromBranchFilter !== 'all' || toBranchFilter !== 'all' || transferType !== 'all' || statusFilter !== 'all' || startDate || endDate) && (
          <button onClick={() => { setSearch(''); setFromBranchFilter('all'); setToBranchFilter('all'); setTransferType('all'); setStatusFilter('all'); setStartDate(''); setEndDate(''); setPage(1); }} className="text-xs text-red-600 hover:underline" data-testid="to-clear-filters">Clear filters</button>
        )}
      </div>

      {/* Transfers List */}
      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="to-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
              <SortableTH label="TO #" field="to_number" sortBy={sortBy} sortDir={sortDir} onSort={(f) => { setSortBy(f); setSortDir(sortBy === f && sortDir === 'desc' ? 'asc' : 'desc'); setPage(1); }} />
              <SortableTH label="From" field="from" sortBy={sortBy} sortDir={sortDir} onSort={(f) => { setSortBy(f); setSortDir(sortBy === f && sortDir === 'desc' ? 'asc' : 'desc'); setPage(1); }} />
              <th className="text-center p-3"></th>
              <SortableTH label="To" field="to" sortBy={sortBy} sortDir={sortDir} onSort={(f) => { setSortBy(f); setSortDir(sortBy === f && sortDir === 'desc' ? 'asc' : 'desc'); setPage(1); }} />
              <SortableTH label="Date" field="date" sortBy={sortBy} sortDir={sortDir} onSort={(f) => { setSortBy(f); setSortDir(sortBy === f && sortDir === 'desc' ? 'asc' : 'desc'); setPage(1); }} />
              <th className="text-center p-3">Type</th>
              <th className="text-center p-3">Items</th>
              <th className="text-center p-3">Status</th>
              <th className="p-3 w-[60px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : transfers.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">No transfer orders found</td></tr>
            ) : transfers.map(t => (
              <tr key={t.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`to-row-${t.id}`}>
                <td className="p-3 font-medium text-[#0F4C81]">{t.to_number}</td>
                <td className="p-3 text-xs">{t.from_branch_name}<br/><span className="text-[10px] text-[#64748B]">{t.from_state_code}</span></td>
                <td className="p-3 text-center"><ArrowsLeftRight size={14} className="text-[#64748B] mx-auto" /></td>
                <td className="p-3 text-xs">{t.to_branch_name}<br/><span className="text-[10px] text-[#64748B]">{t.to_state_code}</span></td>
                <td className="p-3 text-xs">{t.transfer_date}</td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${t.is_inter_state ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'}`}>
                    {t.is_inter_state ? 'Inter-State' : 'Intra-State'}
                  </span>
                </td>
                <td className="p-3 text-center text-xs">{(t.line_items || []).length}</td>
                <td className="p-3 text-center">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-50 text-green-700">{t.status}</span>
                </td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowDetail(t)} data-testid={`to-view-${t.id}`}>
                    <Eye size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="to-pagination">
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <span>Show</span>
            <Select value={String(perPage)} onValueChange={v => { setPerPage(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8" data-testid="to-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100, 200, 500].map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span>per page · {((page - 1) * perPage + 1).toLocaleString('en-IN')}–{Math.min(page * perPage, total).toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8" disabled={page === 1} onClick={() => setPage(1)} data-testid="to-page-first">«</Button>
            <Button variant="outline" size="sm" className="h-8" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="to-page-prev">Prev</Button>
            <span className="text-xs text-[#64748B] px-2">Page {page} of {Math.ceil(total / perPage)}</span>
            <Button variant="outline" size="sm" className="h-8" disabled={page * perPage >= total} onClick={() => setPage(p => p + 1)} data-testid="to-page-next">Next</Button>
            <Button variant="outline" size="sm" className="h-8" disabled={page * perPage >= total} onClick={() => setPage(Math.ceil(total / perPage))} data-testid="to-page-last">»</Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={18} /> Transfer: {showDetail?.to_number}
            </DialogTitle>
          </DialogHeader>
          {showDetail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8FAFC] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[#64748B] uppercase">From</p>
                  <p className="font-semibold text-sm">{showDetail.from_branch_name}</p>
                  <p className="text-xs text-[#64748B]">{showDetail.from_state_code}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[#64748B] uppercase">To</p>
                  <p className="font-semibold text-sm">{showDetail.to_branch_name}</p>
                  <p className="text-xs text-[#64748B]">{showDetail.to_state_code}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><span className="text-[#64748B]">Date:</span> {showDetail.transfer_date}</div>
                <div><span className="text-[#64748B]">Type:</span> {showDetail.is_inter_state ? 'Inter-State' : 'Intra-State'}</div>
                <div><span className="text-[#64748B]">Status:</span> {showDetail.status}</div>
              </div>
              {showDetail.is_inter_state && showDetail.invoice_number && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs">
                  <Lightning size={12} className="inline text-orange-600 mr-1" />
                  Inter-state invoice auto-generated: <strong>{showDetail.invoice_number}</strong>
                </div>
              )}
              {showDetail.line_items?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[#64748B] mb-2">Items Transferred</h4>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[#F8FAFC]">
                      <th className="text-left p-2">Item</th><th className="text-center p-2">Qty</th>
                      <th className="text-right p-2">Rate</th><th className="text-left p-2">Serial #s</th>
                    </tr></thead>
                    <tbody>
                      {showDetail.line_items.map((li, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-2">{li.name}</td>
                          <td className="p-2 text-center">{li.quantity}</td>
                          <td className="p-2 text-right">{fmt(li.rate)}</td>
                          <td className="p-2 text-[10px]">
                            {(li.serial_numbers || []).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {li.serial_numbers.map(sn => (
                                  <SerialHistoryLink key={sn} serialNumber={sn} className="bg-[#E8F5E9] text-[#0F4C81]" />
                                ))}
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {showDetail.notes && <p className="text-xs text-[#64748B]"><strong>Notes:</strong> {showDetail.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Transfer Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">From Branch *</label>
                <SearchableSelect value={form.from_branch_id} onChange={v => setForm(f => ({ ...f, from_branch_id: v }))} placeholder="Source branch..." testid="to-from-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: `${b.name} (${b.state_code})` , keywords: b.code || b.clinic_code || "" }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">To Branch *</label>
                <SearchableSelect value={form.to_branch_id} onChange={v => setForm(f => ({ ...f, to_branch_id: v }))} placeholder="Destination branch..." testid="to-to-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: `${b.name} (${b.state_code})` , keywords: b.code || b.clinic_code || "" }))]} />
              </div>
            </div>

            {fromBranch && toBranch && (
              <div className={`rounded-lg p-3 text-xs font-medium ${isInterState ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                {isInterState
                  ? `Inter-State Transfer (${fromBranch.state_code} → ${toBranch.state_code}): A tax invoice and vendor bill will be auto-generated with IGST.`
                  : `Intra-State Transfer (${fromBranch.state_code} → ${toBranch.state_code}): Only a TO number will be logged.`
                }
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Transfer Date</label>
                <Input type="date" value={form.transfer_date} onChange={e => setForm(f => ({ ...f, transfer_date: e.target.value }))} data-testid="to-date" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes..." data-testid="to-notes" />
              </div>
            </div>

            <h4 className="text-xs font-semibold text-[#0F172A]">Items to Transfer</h4>
            <div style={{ overflow: 'visible' }}>
              <table className="w-full text-sm" data-testid="to-items-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                    <th className="text-left p-2 w-[200px]">Item</th>
                    <th className="text-center p-2 w-[50px]">Qty</th>
                    <th className="text-right p-2 w-[80px]">Rate</th>
                    <th className="text-center p-2 w-[50px]">Tax%</th>
                    <th className="text-left p-2 w-[140px]">Serial #s</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`to-create-line-${idx}`}>
                      <td className="p-2">
                        <ItemSearchPicker items={catalogItems} value={li.name}
                          onPick={(item) => selectCatalogItem(idx, item.id)}
                          testid={`to-item-select-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs" data-testid={`to-qty-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`to-rate-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`to-tax-${idx}`} />
                      </td>
                      <td className="p-2">
                        <div className="relative">
                          {li._availableSerials && li._availableSerials.length > 0 ? (
                            <>
                              <button
                                type="button"
                                onClick={() => updateLineItem(idx, '_showSerialDropdown', !li._showSerialDropdown)}
                                className="w-full h-8 text-xs border border-[#E2E8F0] rounded-md px-2 text-left bg-white hover:border-[#0F4C81] transition-colors flex items-center justify-between"
                                data-testid={`to-serial-btn-${idx}`}
                              >
                                <span className={li.serial_numbers?.length ? 'text-[#0F172A]' : 'text-[#9CA3AF]'}>
                                  {li.serial_numbers?.length ? `${li.serial_numbers.length} selected` : 'Select S/N...'}
                                </span>
                                <svg width="10" height="6" viewBox="0 0 10 6" className={`transition-transform ${li._showSerialDropdown ? 'rotate-180' : ''}`}><path d="M1 1l4 4 4-4" stroke="#64748B" strokeWidth="1.5" fill="none" /></svg>
                              </button>
                              {li._showSerialDropdown && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`to-serial-dropdown-${idx}`}>
                                  {li._availableSerials.map(sn => {
                                    const checked = (li.serial_numbers || []).includes(sn);
                                    return (
                                      <label key={sn} className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#F8FAFC] ${checked ? 'bg-[#EBF3FA]' : ''}`}>
                                        <input type="checkbox" checked={checked} onChange={() => toggleSerial(idx, sn)} className="rounded border-gray-300 text-[#0F4C81] focus:ring-[#0F4C81] h-3.5 w-3.5" />
                                        <span className="font-mono">{sn}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {li.serial_numbers?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {li.serial_numbers.map(sn => (
                                    <span key={sn} className="inline-flex items-center gap-0.5 text-[10px] bg-[#E8F5E9] text-[#0F4C81] px-1.5 py-0.5 rounded font-mono">
                                      {sn}
                                      <button type="button" onClick={() => toggleSerial(idx, sn)} className="text-[#64748B] hover:text-red-500 ml-0.5">&times;</button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <Input value={li._serialInput} onChange={e => updateLineItem(idx, '_serialInput', e.target.value)} placeholder="No serials" className="h-8 text-xs" data-testid={`to-serial-${idx}`} />
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={addLineItem} data-testid="to-add-item">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="to-submit-btn">
                <FloppyDisk size={16} className="mr-2" />{saving ? 'Creating...' : 'Create Transfer Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
